import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../services/api';
import { showToast } from '../context/ToastHandle';
import { getBoardUsage } from '../context/accountUtils';

// inject like pulse CSS once
let __likeCssInjected = false;
function ensureLikeCss() {
  if (__likeCssInjected) return;
  const css = `
  @keyframes like-pulse {
    0% { transform: scale(1); }
    30% { transform: scale(1.15); }
    60% { transform: scale(0.98); }
    100% { transform: scale(1); }
  }
  .like-btn { transition: transform 120ms ease, background 120ms ease, border-color 120ms ease, color 120ms ease; }
  .like-btn.liked { background:#eaf7ec; border-color:#2e7d32; color:#2e7d32; font-weight:600; }
  .like-btn.pulsing { animation: like-pulse 320ms ease; }
  `;
  const style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
  __likeCssInjected = true;
}

const languages = ['all', 'en'];
const ages = ['all', '2-4', '4-7', '8-12', 'kids', 'teen', 'adult'];
const sortOptions = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'az', label: 'A–Z' },
  { value: 'likes', label: 'Likes' },
];

// Map UI sort to backend sort param
function mapSortToServer(sort) {
  switch (sort) {
    case 'likes': return 'popular'; // server "popular" (likes/downloads)
    case 'az':    return 'az';
    case 'relevance':
    default:      return 'recent';
  }
}

export default function Community() {
  const [q, setQ] = useState('');
  const [lang, setLang] = useState('all');
  const [age, setAge] = useState('all');
  const [sort, setSort] = useState('relevance');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pulsingId, setPulsingId] = useState(null);


  // My boards state
  const [myBoardsOpen, setMyBoardsOpen] = useState(false);
  const [myBoards, setMyBoards] = useState([]);
  const token = useMemo(() => localStorage.getItem('token'), []);
  const isAuthed = !!token;

  // Modal state
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null); // selected submission (from any list)
  const [importing, setImporting] = useState(false);
  const [liking, setLiking] = useState(false);

  useEffect(() => { ensureLikeCss(); }, []);

  // Initial: Top liked (limit 10) when empty search
  useEffect(() => {
    fetchResults({ initial: true });
    // eslint-disable-next-line

  }, []);

  // Load "My boards" once if logged in
  useEffect(() => {
    async function loadMine() {
      if (!isAuthed) return;
      try {
        const { data } = await api.get('/community_search.php?owner=me&limit=100', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMyBoards(data?.data || []);
      } catch (e) {
        // silent fail
      }
    }
    loadMine();
  }, [isAuthed, token]);

 async function fetchResults({ initial = false } = {}) { 
  try {
    setLoading(true);

    const params = new URLSearchParams();
    // empty search → load popular top 10
    const isEmpty = (q.trim() === '' && (lang === 'all') && (age === 'all'));
    if (isEmpty && initial) {
      params.set('sort', 'popular');
      params.set('limit', '10');
    } else {
      if (q.trim()) params.set('q', q.trim());
      if (lang !== 'all') params.set('language', lang);
      if (age !== 'all') params.set('age', age);
      const serverSort = mapSortToServer(sort);
      params.set('sort', serverSort);
      params.set('limit', '30');
    }

    // ✅ NEW: include Authorization header and normalize user_liked
    const token = localStorage.getItem('token');
    const { data } = await api.get(
      `/community_search.php?${params.toString()}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
    );

    let rows = data?.data || [];

    // keep rows[i].user_liked as boolean
    rows = rows.map(r => ({ ...r, user_liked: !!r.user_liked }));

    // Client-side sorts
    if (sort === 'az') {
      rows = rows.slice().sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }
    if (!q.trim() && sort === 'likes') {
      rows = rows.slice().sort((a, b) => (b.likes_count || 0) - (a.likes_count || 0));
    }

    setItems(rows);
  } catch (e) {
    console.error(e);
    showToast('Failed to load community boards.', 'error', false);
  } finally {
    setLoading(false);
  }
}


  function openModal(item) {
    setActive(item);
    setOpen(true);
    document.body.classList.add('modal-open');
  }

  function closeModal() {
    setOpen(false);
    setActive(null);
    document.body.classList.remove('modal-open');
  }

  async function onSearchClick() {
    await fetchResults();
  }

  async function handleDislike(submissionId, isDislike = false) {
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('Please log in to rate.', 'error', false);
      return;
    }
    try {
      setLiking(true);
      if (isDislike) {
        await api.post('/community_report.php', { submission_id: submissionId, reason: 'not useful' }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        showToast('Thanks for the feedback.', 'success', false);
      } else {
        await api.post('/community_like.php', { submission_id: submissionId }, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
        // optimistic UI for both grids
        setItems((prev) => prev.map((it) => (it.id === submissionId ? { ...it, likes_count: (it.likes_count || 0) + 1 } : it)));
        setMyBoards((prev) => prev.map((it) => (it.id === submissionId ? { ...it, likes_count: (it.likes_count || 0) + 1 } : it)));
        if (active?.id === submissionId) setActive((a) => a ? { ...a, likes_count: (a.likes_count || 0) + 1 } : a);
        showToast('Liked!', 'success', false);
      }
    } catch (e) {
      console.error(e);
      showToast('Action failed.', 'error', false);
    } finally {
      setLiking(false);
    }
  }

async function handleLike(submissionId) {
  const token = localStorage.getItem('token');
  if (!token) {
    showToast('Please log in to rate.', 'error', false);
    return;
  }
  try {
    setLiking(true);
    const res = await api.post(
      '/community_like.php',
      { submission_id: submissionId },
      { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
    );

    if (res.data?.success) {
      const liked = !!res.data.liked;

      // pulse the clicked id briefly
      setPulsingId(submissionId);
      setTimeout(() => setPulsingId(null), 350);

      // update both lists + modal
      setItems(prev => prev.map(it =>
        it.id === submissionId
          ? { ...it, user_liked: liked, likes_count: (it.likes_count || 0) + (liked ? 1 : -1) }
          : it
      ));
      setMyBoards(prev => prev.map(it =>
        it.id === submissionId
          ? { ...it, user_liked: liked, likes_count: (it.likes_count || 0) + (liked ? 1 : -1) }
          : it
      ));
      if (active?.id === submissionId) {
        setActive(a => a ? { ...a, user_liked: liked, likes_count: (a.likes_count || 0) + (liked ? 1 : -1) } : a);
      }

      showToast(liked ? 'Liked!' : 'Unliked', 'success', false);
    } else {
      showToast('Action failed.', 'error', false);
    }
  } catch (e) {
    console.error(e);
    showToast('Action failed.', 'error', false);
  } finally {
    setLiking(false);
  }
}



// --- Import logic (server-side import) ---
async function handleImport(submissionId) {
  try {
    setImporting(true);
    const token = localStorage.getItem('token');
    if (!token) {
      showToast('Please log in to import boards.', 'error', false);
      return;
    }

    const usage = await getBoardUsage();
    if (usage.isLimitReached) {
      showToast(`Board limit reached for your plan, You have used ${usage.count} of ${usage.limit} boards.`, "error");
      return;
    }

    const { data } = await api.get(`/community_download.php?id=${submissionId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!data?.success) {
      throw new Error(data?.error || 'Import failed');
    }

    const { new_board_id, created_count } = data.data || {};

    showToast(
      created_count > 1
        ? `Imported ${created_count} boards successfully!`
        : 'Board imported successfully!',
      'success',
      false
    );

    // // Optional: Navigate user to the newly imported board view
    // if (new_board_id) {
    //   window.location.href = `/board/${new_board_id}`;
    // }

  } catch (e) {
    console.error(e);
    showToast('Import failed.', 'error', false);
  } finally {
    setImporting(false);
  }
}


  // --- Modal UI ---
  const modal = open && active ? createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000
      }}
      onClick={closeModal}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog" aria-modal="true"
        style={{
          width: '90vw', height: '90vh', background: '#fff', borderRadius: 12,
          display: 'grid', gridTemplateRows: 'auto 1fr auto', overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid #eee' }}>
          <div style={{ fontWeight: 700, fontSize: 18 }}>{active.title}</div>
          <button
            onClick={closeModal}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', fontSize: 24, lineHeight: 1, cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        {/* Image center */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr)', placeItems: 'center', overflow: 'auto', padding: 16 }}>
          <div style={{ maxWidth: '100%', maxHeight: '100%', width: 'min(100%, 1024px)', height: 'auto' }}>
            {active.thumb_url ? (
              <img
                src={active.thumb_url}
                alt={active.title}
                style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 8, objectFit: 'contain' }}
              />
            ) : (
              <div style={{ width: '100%', height: 400, background: '#f3f4f6', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                No preview
              </div>
            )}
          </div>
        </div>

        {/* Footer details */}
        <div style={{ padding: 16, borderTop: '1px solid #eee', display: 'grid', gap: 10 }}>
          {active.description ? (
            <div style={{ whiteSpace: 'pre-wrap' }}>{active.description}</div>
          ) : null}
          <div style={{ color: '#555', fontSize: 13 }}>
            {active.tags_csv ? active.tags_csv.split(',').map(t => t.trim()).filter(Boolean).map((t, i) => (
              <span key={i} style={{ border: '1px solid #ddd', borderRadius: 999, padding: '4px 8px', marginRight: 6 }}>{t}</span>
            )) : <em>No tags</em>}
          </div>

          <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <div>
              <strong>{active.likes_count || 0}</strong> likes &nbsp;&nbsp; <strong>{active.downloads_count || 0}</strong> downloads
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
            <button
              disabled={liking}
              onClick={() => handleLike(active.id)}
              className={`like-btn ${active?.user_liked ? 'liked' : ''} ${pulsingId === active?.id ? 'pulsing' : ''}`}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #ddd',
                background: '#fff',
                cursor: 'pointer',
              }}
              title={active?.user_liked ? 'Unlike' : 'Like'}
            >
              {active?.user_liked ? '💚 Liked' : '👍 Like'}
            </button>
              <button
                disabled={liking}
                onClick={() => handleDislike(active.id, true)}
                style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #f5c2c7', background: '#fff5f5', cursor: 'pointer', color: '#b4232b' }}
                title="Report / Not useful"
              >
                👎 Report
              </button>
              <button
                disabled={importing}
                onClick={() => handleImport(active.id)}
                style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#4CAF50', color: '#fff', cursor: 'pointer', fontWeight: 600 }}
              >
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  // --- Render ---
  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginTop: 0 }}>Community Boards</h2>

      {/* Top controls */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 140px 140px 140px auto', gap: 8, alignItems: 'center', marginBottom: 12 }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search boards…"
          style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }}
        />
        <select value={lang} onChange={(e) => setLang(e.target.value)} style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }}>
          {languages.map(l => <option key={l} value={l}>{l}</option>)}
        </select>
        <select value={age} onChange={(e) => setAge(e.target.value)} style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }}>
          {ages.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value)} style={{ padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8 }}>
          {sortOptions.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <button onClick={onSearchClick} style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: '#111', color: '#fff', cursor: 'pointer' }}>
          Search
        </button>
      </div>

      {/* My boards (collapsible) */}
      {isAuthed && myBoards.length > 0 && (
        <div style={{ 
          marginBottom: 14,
          border: '1px solid rgb(204, 204, 204)',
          borderRadius: 10,
          padding: 10,
          background: '#f3f3f3ff',
          boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
        }}>
          <button
            onClick={() => setMyBoardsOpen(v => !v)}
            style={{ background: 'transparent', border: 'none', fontWeight: 700, fontSize: 16, cursor: 'pointer', padding: 0 }}
            aria-expanded={myBoardsOpen}
          >
            My boards {myBoardsOpen ? '▾' : '▸'}
          </button>

          {myBoardsOpen && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginTop: 8 }}>
              {myBoards.map((it) => (
                <div key={it.id} style={{ border: '1px solid #eee', borderRadius: 10, padding: 10, background: '#fff' }}>
                  <button
                    onClick={() => openModal(it)}
                    style={{ width: '100%', aspectRatio: '1 / 1', border: 'none', padding: 0, margin: 0, background: 'transparent', cursor: 'pointer' }}
                  >
                    <div style={{ width:'100%', height:'100%', background:'#fafafa', borderRadius:8, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center' }}>
                      {it.thumb_url ? (
                        <img src={it.thumb_url} alt={it.title} style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
                      ) : (
                        <span style={{ color:'#999' }}>No preview</span>
                      )}
                    </div>
                  </button>
                  <div style={{ marginTop: 8, fontWeight: 600, overflow: 'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                    {it.title}
                  </div>
                  <div style={{ fontSize: 12, color: '#666' }}>👍 {it.likes_count || 0} &nbsp;&nbsp; ⬇️ {it.downloads_count || 0}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {loading && <div>Loading…</div>}

      {/* Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
        {items.map((it) => (
          <div key={it.id} style={{ 
            border: '1px solid rgb(215, 209, 209)',
            borderRadius: 10,
            padding: 10,
            background: '#f3f3f3ff',
            boxShadow: '5px 7px 20px 3px rgba(0, 0, 0, 0.1)'
            }}>
            <button
              onClick={() => openModal(it)}
              style={{ width: '100%', aspectRatio: '1 / 1', border: 'none', padding: 0, margin: 0, background: 'transparent', cursor: 'pointer' }}
              aria-label={`Preview ${it.title}`}
            >
              <div style={{
                width: '100%', height: '100%', background: '#fafafa', borderRadius: 8, overflow: 'hidden',
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {it.thumb_url ? (
                  <img src={it.thumb_url} alt={it.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <span style={{ color: '#999' }}>No preview</span>
                )}
              </div>
            </button>

            <div style={{ marginTop: 8, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {it.title}
            </div>
            <div style={{ fontSize: 12, color: '#666' }}>
              👍 {it.likes_count || 0} &nbsp;&nbsp; ⬇️ {it.downloads_count || 0}
            </div>
          </div>
        ))}
      </div>

      {modal}
    </div>
  );
}
